import React, { useEffect, useState } from 'react';
import { DayPicker } from 'react-day-picker';
import 'react-day-picker/dist/style.css';
import './style.css';
import { ReactComponent as CalendarIcon } from 'assets/icon/calender-grey.svg';
import moment from 'moment';
import { ReactComponent as CrossIcon } from 'assets/icon/x.svg';
import { useTranslation } from 'react-i18next';

interface IDatePickerProps {
  placement?: 'BOTTOM_LEFT' | 'BOTTOM_RIGHT' | 'TOP_LEFT' | 'TOP_RIGHT';
  disabled?: boolean;
  isEditable?: boolean;
  selected: string | undefined | null;
  onChange: (date: Date | undefined | string) => void;
  dateFormat?: string;
  placeholder?: string;
  error?: string;
  height?: string;
  isAllowPastDate?: boolean;
  isAllowFutureDate?: boolean;
}

const DatePickerComponent: React.FC<IDatePickerProps> = ({
  placement = 'BOTTOM_LEFT',
  disabled = false,
  isEditable = false,
  selected = '',
  onChange,
  dateFormat = 'DD/MM/YYYY',
  placeholder = 'DD/MM/YYYY',
  error = '',
  height = 'h-12',
  isAllowPastDate = true,
  isAllowFutureDate = true,
}) => {
  const [show, setShow] = useState<boolean>(false);
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(
    selected ? new Date(selected) : undefined,
  );
  const { t } = useTranslation();
  useEffect(() => {
    setSelectedDate(selected ? new Date(selected) : undefined);
  }, [selected]);
  const onBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    e.preventDefault();
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setShow(false);
    }
  };

  const handleSelectedDate = (e: Date | undefined) => {
    setSelectedDate(e);
    onChange(e);
    setShow(false);
  };

  return (
    <div className="relative w-full bg-white h-fit" tabIndex={0} onBlur={onBlur}>
      <div
        className={`flex items-center px-4 border-1.5 rounded-1 border-grey focus:border-active outline-none disabled:bg-grey1 w-full ${
          disabled ? 'pointer-events-none bg-grey1' : ''
        } ${error ? 'border-red-500' : ''} h-10 ${`lg:${height}`}`}
        onClick={() => setShow(!show)}
      >
        <input
          type="text"
          className={`flex items-center outline-none text-base placeholder:text-sm text-black2  placeholder:text-grey2 w-full flex-1 
          ${disabled ? 'bg-grey1' : ''} 
          ${!isEditable ? 'pointer-events-none' : ''}
          ${error ? 'border-red-500' : ''}`}
          placeholder={t('DATE_PLACEHOLDER')}
          disabled={disabled}
          value={selectedDate ? moment(selectedDate).format(dateFormat) : ''}
        />
        <div className="flex items-center justify-center border-r-1 pr-2 cursor-pointer">
          <CrossIcon
            height={18}
            width={18}
            className={`flex items-center justify-center`}
            onClick={e => {
              e.stopPropagation();
              onChange('');
            }}
          />
        </div>
        <div
          className={`flex items-center justify-end pl-2 ${
            disabled ? 'cursor-not-allowed' : 'cursor-pointer'
          }`}
        >
          <CalendarIcon height={16} width={16} />
        </div>
      </div>
      {error && <p className="font-regular text-xs text-red-500">{error}</p>}
      {show && (
        <div
          className={`shadow-md rounded-4 absolute z-10 bg-[#FAF9F6] ${
            placement === 'BOTTOM_RIGHT'
              ? 'right-0'
              : placement === 'TOP_LEFT'
              ? 'bottom-0 left-0'
              : placement === 'TOP_RIGHT'
              ? 'bottom-0 right-0'
              : ''
          }`}
        >
          <DayPicker
            mode="single"
            selected={selectedDate}
            onSelect={e => handleSelectedDate(e)}
            captionLayout="dropdown-buttons"
            modifiers={{
              disabled:
                !isAllowFutureDate && !isAllowPastDate
                  ? { before: new Date(), after: new Date() }
                  : !isAllowPastDate
                  ? { before: new Date() }
                  : !isAllowFutureDate
                  ? { after: new Date() }
                  : false,
            }}
            modifiersStyles={{
              selected: {
                background: '#337B9E',
              },
            }}
            fromYear={1900}
            toYear={3050}
          />
        </div>
      )}
    </div>
  );
};

export default DatePickerComponent;
